import { useState } from "react";
import type { RankedSpace, SpaceAdjustment } from "../../ai-mode/types";
import { formatAddressShort, getSpaceDisplayName } from "../utils";
import { useProposalT } from "../i18n/ProposalI18nContext";

interface SpaceComparisonTableProps {
  spaces: RankedSpace[];
  spaceAdjustments?: SpaceAdjustment[];
  selectedSpaceId: string;
  onSelectSpace: (id: string) => void;
  visitSelectedIds: Set<string>;
  onToggleVisit: (id: string) => void;
  onSchedule: () => void;
  scheduledSpaceIds?: Set<string>;
  showScheduling?: boolean;
  hidePrice?: boolean;
}

export function SpaceComparisonTable({
  spaces,
  spaceAdjustments = [],
  selectedSpaceId: _selectedSpaceId,
  onSelectSpace: _onSelectSpace,
  visitSelectedIds,
  onToggleVisit,
  onSchedule,
  scheduledSpaceIds,
  showScheduling = true,
  hidePrice = false,
}: SpaceComparisonTableProps) {
  const { t } = useProposalT();
  const [showCredits, setShowCredits] = useState(false);

  if (!spaces || spaces.length === 0) return null;

  return (
    <section
      id="comparativo"
      className="pt-16 pb-8 px-4 md:px-6 font-space-grotesk"
      style={{ background: "#FFFDF5", borderTop: "1px solid #E8E6E3" }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <span
            style={{
              display: "inline-block",
              padding: "6px 16px",
              borderRadius: "999px",
              fontSize: "13px",
              fontWeight: 500,
              color: "#1A1814",
              border: "1px solid #E8E6E3",
              background: "white",
              marginBottom: "12px",
            }}
          >
            {t("table.badge")}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold mb-3"
            style={{ color: "#1A1814" }}
          >
            {t("table.heading")}
          </h2>
          <p
            className="text-base"
            style={{ color: "#78736D", maxWidth: "540px", margin: "0 auto" }}
          >
            {t("table.subheading")}
          </p>

          {/* Credits toggle */}
          {!hidePrice && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                marginTop: "16px",
                padding: "8px 16px",
                borderRadius: "999px",
                background: "white",
                border: "1px solid #E8E6E3",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: showCredits ? 500 : 600,
                  color: showCredits ? "#78736D" : "#1A1814",
                  transition: "all 0.2s",
                }}
              >
                R$
              </span>
              <button
                onClick={() => setShowCredits((v) => !v)}
                style={{
                  position: "relative",
                  width: "44px",
                  height: "24px",
                  borderRadius: "12px",
                  background: showCredits ? "#FFD900" : "#E8E6E3",
                  border: "none",
                  cursor: "pointer",
                  transition: "background 0.2s",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: "2px",
                    left: showCredits ? "22px" : "2px",
                    width: "20px",
                    height: "20px",
                    borderRadius: "10px",
                    background: "white",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    transition: "left 0.2s",
                  }}
                />
              </button>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: showCredits ? 600 : 500,
                  color: showCredits ? "#1A1814" : "#78736D",
                  transition: "all 0.2s",
                }}
              >
                {t("table.credits")}
              </span>
            </div>
          )}
        </div>

        {/* ── Mobile: card view ── */}
        <div className="md:hidden flex flex-col gap-3 mb-4">
          {spaces.map((space, index) => {
            const adj = spaceAdjustments.find((a) => a.spaceId === space.id);
            const clientPrice = adj?.clientPrice ?? space.monthlyPrice ?? 0;
            const creditsQuantity = adj?.credits ?? space.credits ?? 0;
            const pricePerPos =
              clientPrice > 0 && space.capacity > 0
                ? clientPrice / space.capacity
                : space.pricePerWorkstation;
            const creditsPerPos =
              creditsQuantity > 0 && space.capacity > 0
                ? creditsQuantity / space.capacity
                : null;
            const marketDiscountPct = adj?.marketDiscountPct;
            const isRecommended = !!space.isRecommended;
            const isScheduled = scheduledSpaceIds?.has(space.id) ?? false;
            const isVisitSelected = visitSelectedIds.has(space.id);

            return (
              <div
                key={space.id}
                className="rounded-2xl p-4"
                style={{
                  border: isVisitSelected
                    ? "2px solid #FFD900"
                    : isRecommended
                      ? "1.5px solid #F0D060"
                      : "1px solid #E8E6E3",
                  background: isVisitSelected
                    ? "#FFFBEB"
                    : isRecommended
                      ? "#FFF8E1"
                      : "white",
                }}
              >
                {/* Space name + recommended badge */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <p
                      className="text-sm font-bold"
                      style={{ color: "#1A1814", margin: 0 }}
                    >
                      {getSpaceDisplayName(
                        space,
                        index,
                        t("spaces.option", { number: index + 1 }),
                      )}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: "#78736D" }}>
                      {formatAddressShort(space.address)}
                    </p>
                    {isRecommended && (
                      <span
                        className="text-xs font-semibold mt-1 inline-block"
                        style={{ color: "#B8860B" }}
                      >
                        {t("spaces.recommended")}
                      </span>
                    )}
                  </div>
                  {/* Price per position — highlighted */}
                  <div className="text-right flex-shrink-0">
                    <p
                      className="text-base font-bold"
                      style={{ color: "#B8860B" }}
                    >
                      {showCredits
                        ? creditsPerPos != null
                          ? creditsPerPos.toLocaleString("pt-BR", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          : "—"
                        : pricePerPos > 0
                          ? `R$ ${pricePerPos.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : "—"}
                    </p>
                    <p className="text-xs" style={{ color: "#78736D" }}>
                      {showCredits
                        ? t("table.colCreditsPerPosition")
                        : t("table.colPricePerPosition")}
                    </p>
                    {!showCredits && marketDiscountPct != null && (
                      <span
                        className="inline-block mt-1 text-xs font-medium px-1.5 py-0.5 rounded"
                        style={{ background: "#ECFDF5", color: "#16A34A" }}
                      >
                        {t("table.belowMarket", {
                          pct: String(marketDiscountPct),
                        })}
                      </span>
                    )}
                  </div>
                </div>

                {/* Secondary info row */}
                <div
                  className="flex gap-4 text-xs mb-3 flex-wrap"
                  style={{ color: "#78736D" }}
                >
                  <span>
                    <span
                      className="font-semibold"
                      style={{ color: "#1A1814" }}
                    >
                      {space.capacity}
                    </span>{" "}
                    {t("table.colPositions").toLowerCase()}
                  </span>
                  {space.sizeM2 && (
                    <span>
                      <span
                        className="font-semibold"
                        style={{ color: "#1A1814" }}
                      >
                        {space.sizeM2} m²
                      </span>
                    </span>
                  )}
                  <span>
                    {showCredits
                      ? creditsQuantity > 0
                        ? `${creditsQuantity.toLocaleString("pt-BR")} créditos/mês`
                        : "—"
                      : clientPrice > 0
                        ? `R$ ${clientPrice.toLocaleString("pt-BR")}/mês`
                        : "—"}
                  </span>
                </div>

                {/* Visit button — prominent, full-width */}
                {showScheduling && (
                  <button
                    onClick={
                      isScheduled ? undefined : () => onToggleVisit(space.id)
                    }
                    className="w-full flex items-center justify-center gap-2 rounded-xl font-bold text-sm transition-all duration-200"
                    style={{
                      minHeight: "44px",
                      padding: "10px 16px",
                      background: isScheduled
                        ? "#F5F3EF"
                        : isVisitSelected
                          ? "#FFD900"
                          : "white",
                      color: isScheduled ? "#78736D" : "#1A1814",
                      border: isScheduled
                        ? "1px solid #E8E6E3"
                        : isVisitSelected
                          ? "2px solid #D4B800"
                          : "2px solid #E8E6E3",
                      cursor: isScheduled ? "default" : "pointer",
                    }}
                  >
                    {isScheduled ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#22c55e"
                        strokeWidth="2.5"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    ) : isVisitSelected ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    )}
                    {isScheduled
                      ? t("table.sent")
                      : isVisitSelected
                        ? t("table.selected")
                        : t("table.wantToVisit")}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Desktop: table view ── */}
        <div
          className="hidden md:block"
          style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              minWidth: "980px",
            }}
          >
            <thead>
              <tr style={{ background: "#F5F3EF" }}>
                <th
                  style={{
                    padding: "14px 20px",
                    textAlign: "left",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#78736D",
                  }}
                >
                  {t("table.colSpace")}
                </th>
                <th
                  style={{
                    padding: "14px 16px",
                    textAlign: "left",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#78736D",
                  }}
                >
                  {t("table.colLocation")}
                </th>
                <th
                  style={{
                    padding: "14px 16px",
                    textAlign: "center",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#78736D",
                  }}
                >
                  {t("table.colPositions")}
                </th>
                <th
                  style={{
                    padding: "14px 16px",
                    textAlign: "center",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#78736D",
                  }}
                >
                  {t("table.colArea")}
                </th>
                {!hidePrice && (
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#78736D",
                    }}
                  >
                    {showCredits
                      ? t("table.colCreditsPerPosition")
                      : t("table.colPricePerPosition")}
                  </th>
                )}
                {!hidePrice && (
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#78736D",
                    }}
                  >
                    {showCredits
                      ? t("table.colCreditsMonth")
                      : t("table.colMonthly")}
                  </th>
                )}
                {showScheduling && (
                  <th
                    style={{
                      padding: "14px 20px",
                      textAlign: "center",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#78736D",
                    }}
                  >
                    {t("table.colVisit")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {spaces.map((space, index) => {
                const adj = spaceAdjustments.find(
                  (a) => a.spaceId === space.id,
                );
                const clientPrice = adj?.clientPrice ?? space.monthlyPrice ?? 0;
                const creditsQuantity = adj?.credits ?? space.credits ?? 0;
                const pricePerPos =
                  clientPrice > 0 && space.capacity > 0
                    ? clientPrice / space.capacity
                    : space.pricePerWorkstation;
                const creditsPerPos =
                  creditsQuantity > 0 && space.capacity > 0
                    ? creditsQuantity / space.capacity
                    : null;
                const marketDiscountPct = adj?.marketDiscountPct;
                const isRecommended = !!space.isRecommended;
                const isScheduled = scheduledSpaceIds?.has(space.id) ?? false;
                const isVisitSelected = visitSelectedIds.has(space.id);
                const rowBg = isVisitSelected
                  ? "#FFFBEB"
                  : isRecommended
                    ? "#FFF8E1"
                    : "white";
                const borderColor = "#F0E9D8";

                return (
                  <tr key={space.id} style={{ background: rowBg }}>
                    {/* Espaço */}
                    <td
                      style={{
                        padding: "18px 20px",
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "15px",
                            fontWeight: 700,
                            color: "#1A1814",
                          }}
                        >
                          {getSpaceDisplayName(
                            space,
                            index,
                            t("spaces.option", { number: index + 1 }),
                          )}
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#9E9A94",
                          }}
                        >
                          {formatAddressShort(space.address)}
                        </span>
                        {isRecommended && (
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "#B8860B",
                              letterSpacing: "0.03em",
                            }}
                          >
                            {t("spaces.recommended")}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Localização */}
                    <td
                      style={{
                        padding: "18px 16px",
                        fontSize: "13px",
                        color: "#555",
                        borderBottom: `1px solid ${borderColor}`,
                        maxWidth: "180px",
                      }}
                    >
                      {formatAddressShort(space.address) || "—"}
                    </td>

                    {/* Posições */}
                    <td
                      style={{
                        padding: "18px 16px",
                        textAlign: "center",
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#1A1814",
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      {space.capacity}
                    </td>

                    {/* Área */}
                    <td
                      style={{
                        padding: "18px 16px",
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#555",
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      {space.sizeM2 ? `${space.sizeM2} m²` : "—"}
                    </td>

                    {/* R$/Posição ou Créditos/Posição */}
                    {!hidePrice && (
                      <td
                        style={{
                          padding: "18px 16px",
                          textAlign: "center",
                          fontSize: "15px",
                          fontWeight: 700,
                          color: "#B8860B",
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        {showCredits
                          ? creditsPerPos != null
                            ? creditsPerPos.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : "—"
                          : pricePerPos > 0
                            ? `R$ ${pricePerPos.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : "—"}
                        {!showCredits && marketDiscountPct != null && (
                          <span
                            style={{
                              display: "block",
                              marginTop: "4px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              background: "#ECFDF5",
                              color: "#16A34A",
                              fontSize: "11px",
                              fontWeight: 500,
                            }}
                          >
                            {t("table.belowMarket", {
                              pct: String(marketDiscountPct),
                            })}
                          </span>
                        )}
                      </td>
                    )}

                    {/* Mensalidade ou Créditos/Mês */}
                    {!hidePrice && (
                      <td
                        style={{
                          padding: "18px 16px",
                          textAlign: "center",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#1A1814",
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        {showCredits
                          ? creditsQuantity > 0
                            ? creditsQuantity.toLocaleString("pt-BR")
                            : "—"
                          : clientPrice > 0
                            ? `R$ ${clientPrice.toLocaleString("pt-BR")}`
                            : "—"}
                      </td>
                    )}

                    {/* Visitar */}
                    {showScheduling && (
                      <td
                        style={{
                          padding: "18px 16px",
                          textAlign: "center",
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        <button
                          onClick={
                            isScheduled
                              ? undefined
                              : () => onToggleVisit(space.id)
                          }
                          style={{
                            padding: "8px 18px",
                            borderRadius: "999px",
                            background: isScheduled
                              ? "#F5F3EF"
                              : isVisitSelected
                                ? "#FFD900"
                                : "white",
                            color: isScheduled ? "#78736D" : "#1A1814",
                            fontSize: "12px",
                            fontWeight: 600,
                            border: isScheduled
                              ? "1px solid #E8E6E3"
                              : isVisitSelected
                                ? "1.5px solid #D4B800"
                                : "1px solid #E8E6E3",
                            cursor: isScheduled ? "default" : "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.2s",
                          }}
                        >
                          {isScheduled ? (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#22c55e"
                              strokeWidth="2.5"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          ) : isVisitSelected ? (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          ) : (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <rect x="3" y="4" width="18" height="18" rx="2" />
                              <line x1="16" y1="2" x2="16" y2="6" />
                              <line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                          )}
                          {isScheduled
                            ? t("table.sent")
                            : isVisitSelected
                              ? t("table.selected")
                              : t("table.wantToVisit")}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Best price guarantee */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: "12px",
            padding: "0 20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#22C55E"
              strokeWidth="2.5"
              style={{ flexShrink: 0 }}
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span
              style={{ fontSize: "12px", fontWeight: 600, color: "#22C55E" }}
            >
              {t("table.bestPriceGuarantee")}
            </span>
            <span style={{ fontSize: "11px", color: "#B8B3AD" }}>
              {t("table.bestPriceDesc")}
            </span>
          </div>
        </div>

        {/* Market discount footnote */}
        {!showCredits &&
          spaceAdjustments.some((a) => a.marketDiscountPct != null) && (
            <p
              style={{
                textAlign: "right",
                fontSize: "11px",
                color: "#B8B3AD",
                marginTop: "8px",
                paddingRight: "20px",
              }}
            >
              {t("table.marketSource")}
            </p>
          )}

        {/* Inline visit action bar — shown when spaces are selected */}
        {showScheduling && visitSelectedIds.size > 0 && (
          <div
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            style={{
              marginTop: "20px",
              background: "#FFD900",
              color: "#1A1814",
              borderRadius: "16px",
              padding: "14px 24px",
            }}
          >
            <span
              className="flex items-center gap-2"
              style={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#1A1814",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <strong>{visitSelectedIds.size}</strong>{" "}
              {visitSelectedIds.size === 1
                ? t("table.visitBarSingular")
                : t("table.visitBarPlural")}
            </span>
            <button
              onClick={onSchedule}
              className="w-full md:w-auto"
              style={{
                padding: "10px 24px",
                borderRadius: "999px",
                background: "#1A1814",
                color: "white",
                fontSize: "13px",
                fontWeight: 700,
                border: "none",
                cursor: "pointer",
                transition: "all 0.2s",
                minHeight: "44px",
              }}
            >
              {t("table.scheduleVisits")}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
